/* Copyright (c) 2020-present CloudIO Inc */

import { ActionProps, AnyColumn, Platform } from 'componentregistry';
import { getUrl } from 'config/config';
import { userAtom } from 'root/useUser';
import { getURLQueryStringToPost } from 'utils/url';
import { isEmpty, isNotEmpty } from 'utils/isEmpty';
import FileSaver from 'file-saver';
import { userTimeZoneAtom } from '../../utils/state';
import { PivotTableItem } from 'componentregistry/components/PivotTable/PivotTable';

async function ExportData(
  props: ActionProps<'ActionExportData'>,
): Promise<boolean> {
  const {
    appUid,
    platform,
    pageId,
    targetItemId,
    itemId,
    async,
    format,
    fileNameExpression,
    downloadAsZip,
    enableEmailAlerts,
    emailSubjectExpression,
    emailBodyExpression,
    templateCodeExpression,
    headerNameExpression,
    showReportInformation,
    exportFooter,
  } = props;
  const item = platform.getItem(appUid, pageId, targetItemId);
  const fileName = platform.evalIfExpressionString(
    appUid,
    pageId,
    itemId,
    'fileNameExpression',
    { ...props },
    fileNameExpression,
  );
  const headerName = platform.evalIfExpressionString(
    appUid,
    pageId,
    itemId,
    'headerNameExpression',
    { ...props },
    headerNameExpression,
  );
  const customTemplateCode = platform.evalIfExpressionString(
    appUid,
    pageId,
    itemId,
    'templateCodeExpression',
    { ...props },
    templateCodeExpression,
  );
  if (!item?.type) {
    platform.showError(
      `Developer Error: Invalid Target Item ID [${targetItemId}] passed to "Action Export"`,
      { pageId, itemId, appUid },
    );
    return false;
  }
  const store = platform.getParentStore(appUid, pageId, targetItemId);
  if (!store) {
    platform.showError(
      `Developer Error: Missing store for item with Item ID: [${targetItemId}] passed to "Action Export"`,
      { pageId, itemId, appUid },
    );
    return false;
  }
  const user = platform.getter(userAtom);
  if (!user) {
    platform.showError(
      `Missing user session! Cannot invoke export data action with itemID [${itemId}].`,
      { pageId, itemId, appUid },
    );
    return false;
  }
  const tz =
    platform.getter(userTimeZoneAtom, {
      params: { userName: user.userName },
    }) || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { ds, alias } = store;
  if (!store.previousQueryBody) {
    platform.showError(
      `Datasource [${ds}] with alias [${alias}] not queried yet! Cannot invoke export data action with itemID [${itemId}].`,
      { pageId, itemId, appUid },
    );
    return false;
  }
  const gridColumns = (
    platform.getChildrenItemsById(
      appUid,
      pageId,
      targetItemId,
      (child): boolean => true,
    ) as AnyColumn[]
  ).filter((child) => {
    return (
      child.type.includes('Column') &&
      child.props.viewAttribute &&
      child.type !== 'DivColumn' &&
      (isEmpty(child.props.visibleCondition)
        ? child.props.visible
        : child.props.visible &&
          platform.evalBoolean(
            appUid,
            pageId,
            child.itemId,
            'visibleCondition',
            { ...child.props },
            child.props.visibleCondition,
          ))
    );
  });
  const columnsJson: Record<string, unknown>[] = [];
  gridColumns.forEach((column) => {
    columnsJson.push(getColumn(column, platform, appUid, pageId));
  });
  const url = `${getUrl()}export?${getURLQueryStringToPost(user)}`;
  const emailSubject = platform.evalIfExpressionString(
    appUid,
    pageId,
    itemId,
    'emailSubjectExpression',
    { ...props },
    emailSubjectExpression,
  );
  const emailBody = platform.evalIfExpressionString(
    appUid,
    pageId,
    itemId,
    'emailBodyExpression',
    { ...props },
    emailBodyExpression,
  );
  const data = {
    dataSourceRequest: {
      [alias]: {
        ...store.previousQueryBody?.[alias],
      },
    },
    headerName: headerName ?? '',
    fileName,
    columnsJson,
    customTemplateCode,
    format: { formatType: format },
    isAsync: async,
    tz,
    downloadAsZip,
    enableEmailAlerts,
    emailSubject: emailSubject || null,
    emailBody: emailBody || null,
    exportFooter,
    showReportInformation,
    gridType: item.type,
    pivotConfiguration: {
      configuration:
        item.type === 'PivotTable'
          ? (item as PivotTableItem).props.configuration
          : undefined,
    },
  };

  if (async) {
    platform.setter(['transient', 'asyncExport'], {
      appUid,
      itemId,
      parentPageId: pageId,
      datasourceUid: ds,
      data,
    });
    return true;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/csv',
      Authorization: user.jwt,
      'X-Application': appUid,
    },
    body: JSON.stringify(data),
  });
  if (response.status === 200) {
    if (async) {
      platform.showInfo(
        'Async export request has been submitted successfully. You will get an email once export is done.',
      );
      return true;
    }
    const blob = await response.blob();
    let extension = 'zip';
    if (!downloadAsZip) {
      if (format === 'XLSX') {
        extension = 'xlsx';
      } else if (format === 'CSV') {
        extension = 'csv';
      }
    }
    FileSaver.saveAs(blob, `${fileName || ds}.${extension}`);
  } else {
    const json = await response.json();
    platform.showError(
      `Export Error: ${json.message} for item with Item ID [${itemId}]`,
      { pageId, itemId, appUid },
    );
  }
  return true;
}

export function getColumn(
  column: AnyColumn,
  platform: Platform,
  appUid: string,
  pageId: string,
): Record<string, unknown> {
  const labelResult =
    platform.evalIfExpressionString(
      appUid,
      pageId,
      column.itemId,
      'label',
      {},
      column.props.label,
    ) ?? '';
  const groupHeaderResult =
    platform.evalIfExpressionString(
      appUid,
      pageId,
      column.itemId,
      'label',
      {},
      column.props.header1,
    ) ?? '';
  let colJson: {
    viewAttribute?: string;
    itemType: string;
    label?: string;
    width?: number;
    showTime?: boolean;
    format?: string;
    groupHeader?: string;
    columnFooter?: {
      summaryFunction:
        | 'sum'
        | 'avg'
        | 'min'
        | 'max'
        | 'count'
        | 'none'
        | undefined;
      formatExpression: string | undefined;
    };
  } = {
    viewAttribute: column.props.viewAttribute,
    itemType: column.type,
    label: labelResult,
    width: column.props.width,
    groupHeader: groupHeaderResult,
  };
  switch (column.type) {
    case 'DateColumn':
      const { showTime, format: df } = column.props;
      colJson = { ...colJson, showTime, ...(isNotEmpty(df) && { df }) };
      break;
    case 'NumberColumn':
      const { format } = column.props;
      const columnFooter = platform.getChildrenOfTypeById(
        appUid,
        pageId,
        column.itemId,
        'ColumnFooter',
      );
      const columnFooterPropsArray = columnFooter.map((footer) => ({
        summaryFunction: footer?.props?.summaryFunction,
        formatExpression: footer?.props?.formatExpression,
        excelSummaryFunction: footer?.props?.excelSummaryFunction,
        excelSummaryFormatter: footer?.props?.excelSummaryFormatter,
      }));
      colJson = {
        ...colJson,
        ...(isNotEmpty(format) && { format }),
        columnFooter: columnFooterPropsArray[0],
      };
      break;
  }
  return colJson;
}

export default ExportData;
